import React, { useState, useRef, useEffect } from 'react';
import { Bell, Megaphone, ChevronRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router';
import { useActiveAnnouncements } from '../hooks/useAnnouncements';
import { AnnouncementDetailModal } from './AnnouncementDetailModal';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import type { Announcement } from '@lms/shared/types';

export function NotificationPopover() {
  const { data: announcements } = useActiveAnnouncements({
    targetAudience: 'ADMIN_ONLY',
  });
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { language, t } = useLanguage();

  const items = Array.isArray(announcements) ? announcements.filter(Boolean) : [];
  const count = items.length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date?: Date | string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t(copy.shell.notifications)}
        className="relative text-on-surface-variant hover:text-primary transition-colors p-2 rounded-full hover:bg-surface-container-high focus:outline-none"
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-primary rounded-full ring-2 ring-white animate-pulse" />
        )}
      </button>

      {/* Popover Dropdown */}
      {isOpen && (
        <div
          className={`absolute ${
            language === 'ar' ? 'left-0' : 'right-0'
          } mt-2 w-80 sm:w-96 bg-surface-container-lowest rounded-2xl shadow-level4 border border-on-surface/5 overflow-hidden z-50 animate-fadeIn`}
        >
          {/* Header */}
          <div className="px-5 py-4 bg-surface-container-low/50 flex items-center justify-between border-b border-on-surface/5">
            <div className="flex items-center gap-2">
              <Megaphone size={16} className="text-primary" />
              <h3 className="font-headline font-bold text-sm text-on-surface m-0">
                <Bilingual {...copy.announcements.notificationsTitle} />
              </h3>
            </div>
            {count > 0 && (
              <span className="text-[10px] font-label font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {count}
              </span>
            )}
          </div>

          {/* List Items */}
          <div className="max-h-80 overflow-y-auto divide-y divide-on-surface/5">
            {items.length > 0 ? (
              items.slice(0, 5).map(item => (
                <div
                  key={item.id}
                  onClick={() => {
                    setSelectedAnnouncement(item);
                    setIsOpen(false);
                  }}
                  className="p-4 hover:bg-surface-container-high transition-colors cursor-pointer group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      {item.priority === 'URGENT' && (
                        <span className="text-error">
                          <AlertCircle size={13} />
                        </span>
                      )}
                      <span className="font-body font-bold text-xs text-on-surface group-hover:text-primary transition-colors line-clamp-1">
                        {item.title}
                      </span>
                    </div>
                    <span className="text-[10px] font-body text-on-surface-variant opacity-60 shrink-0">
                      {formatDate(item.createdAt)}
                    </span>
                  </div>
                  <p className="text-[11px] font-body text-on-surface-variant line-clamp-2 mt-1 opacity-75">
                    {item.content}
                  </p>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-xs font-body text-on-surface-variant">
                <Bilingual {...copy.announcements.noNotifications} />
              </div>
            )}
          </div>

          {/* Footer Link */}
          <div className="p-3 bg-surface-container-low/30 text-center border-t border-on-surface/5">
            <Link
              to="/announcements"
              onClick={() => setIsOpen(false)}
              className="text-xs font-label font-bold text-primary hover:underline flex items-center justify-center gap-1.5"
            >
              <Bilingual {...copy.announcements.viewAll} />
              <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      )}

      <AnnouncementDetailModal
        announcement={selectedAnnouncement}
        isOpen={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
      />
    </div>
  );
}
